import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

interface Product {
  id: string;
  name: string;
  sellingPrice: number;
  category: string;
  isService?: boolean;
  sku?: string;
  description?: string;
  duration?: number;
  stock?: number;
  tags?: string[];
}

interface ProductComboboxProps {
  products: Product[];
  selectedProduct: Product | null;
  onSelect: (product: Product) => void;
  onCreateRequested?: (query: string) => void;
  placeholder?: string;
  dense?: boolean;
  /** Personaliza el texto de la opción "crear/usar" (ej: texto libre sin crear producto) */
  createLabelOverride?: (query: string) => string;
  /** Texto libre guardado (cuando no hay producto seleccionado) para mostrarlo en el input */
  freeText?: string;
}

export default function ProductCombobox({
  products,
  selectedProduct,
  onSelect,
  onCreateRequested,
  placeholder = 'Buscar producto o servicio...',
  dense = false,
  createLabelOverride,
  freeText
}: ProductComboboxProps) {
  const [query, setQuery] = useState('');
  const lastSelectionRef = useRef<{ id: string; at: number } | null>(null);
  // El usuario está clickeando/tocando una opción de la lista: el mousedown
  // ocurre ANTES del blur del input, así que sirve para frenar el auto-create
  // de texto libre (si no, se agregaban el texto tipeado Y el producto elegido).
  const pickingFromListRef = useRef<number>(0);

  useEffect(() => {
    if (selectedProduct?.name) {
      setQuery(selectedProduct.name);
    } else if (freeText) {
      setQuery(freeText);
    }
  }, [selectedProduct?.id, selectedProduct?.name, freeText]);

  const MAX_VISIBLE = 30;

  // Las etiquetas (normas API/ACEA/VW, aprobaciones de caja) tambien cuentan:
  // cargando un presupuesto se busca el aceite por lo que pide el auto.
  const coincide = (product: Product, q: string) => {
    const tags = ((product as any).tags || []).join(' ').toLowerCase();
    return (
      product.name.toLowerCase().includes(q) ||
      (product.sku && product.sku.toLowerCase().includes(q)) ||
      (product.description && product.description.toLowerCase().includes(q)) ||
      (product.category && product.category.toLowerCase().includes(q)) ||
      tags.includes(q)
    );
  };

  const filteredProducts = useMemo(() => {
    // Products arrive pre-sorted (services first, then alphabetically)
    if (!query) return products.slice(0, MAX_VISIBLE);
    const searchQuery = query.toLowerCase();
    const results: Product[] = [];
    for (const product of products) {
      if (coincide(product, searchQuery)) {
        results.push(product);
        if (results.length >= MAX_VISIBLE) break;
      }
    }
    return results;
  }, [products, query]);

  const totalMatches = useMemo(() => {
    if (!query) return products.length;
    const searchQuery = query.toLowerCase();
    return products.filter((product) => coincide(product, searchQuery)).length;
  }, [products, query]);

  const trimmedQuery = query.trim();
  const showCreateOption = !!onCreateRequested && trimmedQuery.length > 0;

  const createLabel = useMemo(() => {
    if (createLabelOverride) return createLabelOverride(trimmedQuery);
    return trimmedQuery ? `Crear "${trimmedQuery}"...` : 'Crear item...';
  }, [trimmedQuery, createLabelOverride]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const optionPadding = dense ? 'px-3 py-2' : 'px-4 py-3';
  const optionText = dense ? 'text-sm' : 'text-sm';

  return (
    <Combobox
      value={selectedProduct ? selectedProduct.id : null}
      onChange={(value: string | { id?: string }) => {
        const rawId = typeof value === 'string' ? value : value?.id || '';
        if (!rawId) return;

        if (rawId.startsWith('__create__')) {
          // HeadlessUI puede disparar onChange dos veces seguidas: ignorar el
          // create repetido dentro de una ventana corta (si no, se duplica la fila).
          const now = Date.now();
          if (lastSelectionRef.current && lastSelectionRef.current.id === rawId && now - lastSelectionRef.current.at < 600) {
            return;
          }
          // Marcar la "selección" para que el onBlur no dispare otro
          // onCreateRequested (si no, el ítem de texto libre se duplica).
          lastSelectionRef.current = { id: rawId, at: now };
          onCreateRequested?.(query.trim());
          setQuery('');
          return;
        }

        const product = products.find((p) => p.id === rawId);
        if (!product) return;

        // HeadlessUI can fire repeated change events in quick succession.
        // Ignore same-product repeats that happen within a very short window.
        const now = Date.now();
        if (
          lastSelectionRef.current &&
          lastSelectionRef.current.id === rawId &&
          now - lastSelectionRef.current.at < 350
        ) {
          return;
        }
        lastSelectionRef.current = { id: rawId, at: now };

        onSelect(product);
        setQuery('');
      }}
    >
      <div className="relative z-50">
        <div className="relative">
          <Combobox.Input
            className="w-full px-4 py-2 pl-10 pr-10 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            placeholder={placeholder}
            displayValue={(value) => {
              const productId = typeof value === 'string' ? value : (value as Product | null)?.id;
              const product = products.find((p) => p.id === productId);
              return product?.name || selectedProduct?.name || freeText || query;
            }}
            onChange={(event) => setQuery(event.target.value)}
            onBlur={() => {
              // Auto-confirmar texto libre al salir del campo (si no se eligio un producto).
              if (!onCreateRequested) return;
              // Si el blur es porque se está clickeando una opción de la lista,
              // no crear texto libre: la selección la maneja onChange.
              if (Date.now() - pickingFromListRef.current < 1000) return;
              const q = query.trim();
              if (!q) return;
              // Si lo tipeado coincide con un producto del catalogo, NO crear texto libre:
              // el usuario debe elegir el producto (evita duplicar la fila cuando el click de
              // selección y el blur ocurren casi juntos).
              const qLower = q.toLowerCase();
              const matchesProduct = products.some((p) =>
                p.name.toLowerCase() === qLower || (p.sku && p.sku.toLowerCase() === qLower)
              );
              if (matchesProduct) return;
              window.setTimeout(() => {
                // Si recien se selecciono un producto del catalogo, no pisar con texto libre.
                if (lastSelectionRef.current && Date.now() - lastSelectionRef.current.at < 500) return;
                // Segunda chance del guard: el click en la lista pudo llegar despues del blur.
                if (Date.now() - pickingFromListRef.current < 1000) return;
                // Si ya coincide con lo seleccionado/guardado, no repetir.
                if (selectedProduct && selectedProduct.name === q) return;
                if (freeText && freeText === q) return;
                onCreateRequested(q);
              }, 200);
            }}
          />

          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <Combobox.Button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg shadow-lg border bg-card"
          onMouseDown={() => { pickingFromListRef.current = Date.now(); }}
          onTouchStart={() => { pickingFromListRef.current = Date.now(); }}
        >
          {filteredProducts.length === 0 && !showCreateOption ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              No se encontraron productos o servicios
            </div>
          ) : (
            (() => {
              const services = filteredProducts.filter((p) => p.isService || p.category === 'servicio');
              const prods = filteredProducts.filter((p) => !p.isService && p.category !== 'servicio');
              const sections: React.ReactNode[] = [];

              if (services.length > 0) {
                sections.push(
                  <div key="__srv_header" className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 uppercase tracking-wider sticky top-0 z-10 border-b">
                    Servicios
                  </div>
                );
                services.forEach((product) => {
                  sections.push(
                    <Combobox.Option key={product.id} value={product.id} as={Fragment}>
                      {({ active, selected }) => (
                        <li className={`relative cursor-pointer select-none ${optionPadding} ${optionText} ${active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{product.name}</span>
                              {selected && <CheckIcon className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
                            </div>
                            <div className="shrink-0 text-xs font-medium text-purple-600 dark:text-purple-400">{formatCurrency(product.sellingPrice)} Srv</div>
                          </div>
                        </li>
                      )}
                    </Combobox.Option>
                  );
                });
              }

              if (prods.length > 0) {
                if (services.length > 0) {
                  sections.push(
                    <div key="__prod_header" className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 uppercase tracking-wider border-y">
                      Productos
                    </div>
                  );
                }
                prods.forEach((product) => {
                  sections.push(
                    <Combobox.Option key={product.id} value={product.id} as={Fragment}>
                      {({ active, selected }) => (
                        <li className={`relative cursor-pointer select-none ${optionPadding} ${optionText} ${active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{product.name}</span>
                              {selected && <CheckIcon className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
                            </div>
                            <div className="shrink-0 text-xs text-muted-foreground">{formatCurrency(product.sellingPrice)}</div>
                          </div>
                        </li>
                      )}
                    </Combobox.Option>
                  );
                });
              }

              return sections;
            })()
          )}

          {totalMatches > filteredProducts.length && (
            <div className="px-3 py-1.5 text-xs text-center text-muted-foreground bg-muted/30 border-t">
              Mostrando {filteredProducts.length} de {totalMatches} resultados — escribí para filtrar
            </div>
          )}

          {showCreateOption && (
            <Combobox.Option value="__create__" as={Fragment}>
              {({ active }) => (
                <li
                  className={`${optionPadding} ${optionText} cursor-pointer select-none font-medium border-t ${
                    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                >
                  {createLabel}
                </li>
              )}
            </Combobox.Option>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
